import { ErrorHandler, Injectable } from '@angular/core';
import { environment } from '../environments/environment';

type SentryModule = typeof import('./sentry-sdk');

/**
 * The in-app browser a visitor is in, from the user agent: most landing
 * traffic arrives from ad clicks inside these apps, and their webviews behave
 * differently from Safari/Chrome (file pickers especially). '' = a normal
 * browser.
 */
export function inAppBrowser(userAgent: string): string {
  const ua = userAgent || '';
  if (/Instagram/i.test(ua)) {
    return 'instagram';
  }
  if (/FBAN|FBAV|FB_IAB/i.test(ua)) {
    return 'facebook';
  }
  if (/Snapchat/i.test(ua)) {
    return 'snapchat';
  }
  if (/musical_ly|Bytedance|TikTok/i.test(ua)) {
    return 'tiktok';
  }
  return '';
}

/** The lower-cased extension of a file name ('' when it has none). */
export function fileExtension(name: string): string {
  const match = /\.([a-z0-9]{1,8})$/i.exec(name || '');
  return match ? match[1].toLowerCase() : '';
}

let sentry: SentryModule | null = null;
let active = false;
/** Reports made before the SDK arrives; replayed once it loads (bounded). */
const pending: Array<(s: SentryModule) => void> = [];
const MAX_PENDING = 30;

function withSentry(report: (s: SentryModule) => void): void {
  if (sentry) {
    report(sentry);
  } else if (active && pending.length < MAX_PENDING) {
    pending.push(report);
  }
}

/**
 * Browser-only error reporting for uptal.com, LAZY: the SDK is a separate
 * chunk fetched once the page has loaded and the browser is idle, so the first
 * paint every ad click waits on carries none of it. Errors only — no tracing,
 * no session replay (the org's replay quota is tiny). Events land in the
 * frontend project tagged `surface: landing` and `in_app`.
 */
export function initSentry(): void {
  const config = environment.sentry;
  if (!config?.enabled || typeof window === 'undefined') {
    return;
  }
  active = true;
  const load = (): void => {
    import('./sentry-sdk')
      .then((S) => {
        S.init({
          dsn: config.dsn,
          environment: config.environment,
          sampleRate: 1.0,
          tracesSampleRate: 0,
          // Our own bundles only: third-party tags (GTM, pixels, CleverTap, chat)
          // and browser extensions throw constantly and are not ours to fix.
          allowUrls: [/https?:\/\/([a-z0-9-]+\.)*uptal\.com/i],
          ignoreErrors: [
            'ResizeObserver loop limit exceeded',
            'ResizeObserver loop completed with undelivered notifications',
            'Non-Error promise rejection captured',
          ],
          initialScope: {
            tags: {
              surface: 'landing',
              in_app: inAppBrowser(navigator.userAgent) || 'none',
            },
          },
        });
        sentry = S;
        pending.splice(0).forEach((report) => report(S));
      })
      .catch(() => {
        active = false;
        pending.length = 0;
      });
  };
  const whenIdle = (): void => {
    const idle = (window as Window & { requestIdleCallback?: Function })
      .requestIdleCallback;
    if (typeof idle === 'function') {
      idle(load, { timeout: 4000 });
    } else {
      setTimeout(load, 1500);
    }
  };
  if (document.readyState === 'complete') {
    whenIdle();
  } else {
    window.addEventListener('load', whenIdle, { once: true });
  }
}

/**
 * A landing evidence event (never throws). Carries no file names or personal
 * data — only extensions, sizes, statuses and counts.
 */
export function landingEvidence(
  message: string,
  level: 'info' | 'warning' | 'error',
  data: Record<string, string | number | null> = {},
): void {
  withSentry((S) => {
    try {
      S.captureMessage(message, {
        level,
        extra: data,
        tags: { landing_event: message },
      });
    } catch {
      /* reporting must never break the page */
    }
  });
}

/** A breadcrumb on the upload path, so any later event carries the steps that led to it. */
export function landingBreadcrumb(
  message: string,
  data: Record<string, string | number> = {},
): void {
  withSentry((S) => {
    try {
      S.addBreadcrumb({ category: 'upload', message, level: 'info', data });
    } catch {
      /* reporting must never break the page */
    }
  });
}

/**
 * Angular hands component errors to its ErrorHandler instead of the window;
 * this one still logs them and forwards them to Sentry once it has loaded (a
 * no-op on the server, where Sentry never starts).
 */
@Injectable()
export class LandingErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    console.error(error);
    const original =
      (error as { originalError?: unknown })?.originalError ?? error;
    withSentry((S) => {
      try {
        S.captureException(original);
      } catch {
        /* reporting must never break the page */
      }
    });
  }
}
